import { InventoryAxios } from "api/InventoryAxios";
import { WarehouseAxios } from "api/WarehouseAxios";
import axios from "axios";
import FileUploadInput from "Common/Components/FileUploadInput/FileUploadInput";
import FormBreadcrumb from "Common/Components/FormBreadcrumb/FormBreadcrumb";
import Colors from "Constants/Color";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addBlock as onAddBlock } from "slices/block/thunk";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

interface PackingFormValues {
  // packing_id: string;
  packing_date: string;
  item_request_id: string;
  documents: File | null; // Use File[] if allowing multiple uploads
  prepared_by: string;
  no_of_box: string; // Can be number if you're converting inputs before submit
  total_box_weight: string;
  no_of_pallets: string;
  total_pallet_weight: string;
  remarks: string;
}

const CreateWatehouseItemRequestPacking: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [items, setItems] = useState<any>([]);
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const handleOpenModal = (item: any) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const formik = useFormik<PackingFormValues>({
    initialValues: {
      // packing_id: '',
      packing_date: "",
      item_request_id: "",
      documents: null, // or [] if it's multiple files
      prepared_by: "",
      no_of_box: "",
      total_box_weight: "",
      no_of_pallets: "",
      total_pallet_weight: "",
      remarks: "",
    },
    validationSchema: Yup.object({
      // packing_id: Yup.string()
      //   .required('Packing ID is required'),

      packing_date: Yup.date().required("Packing Date is required"),

      item_request_id: Yup.string().required("Item Request ID is required"),

      documents: Yup.mixed().nullable(),

      prepared_by: Yup.string().required("Prepared By is required"),

      no_of_box: Yup.number()
        .typeError("No of Box must be a number")
        .positive("Must be greater than zero")
        .required("No of Box is required"),

      total_box_weight: Yup.number()
        .typeError("Total Box Weight must be a number")
        .positive("Must be greater than zero")
        .required("Total Box Weight is required"),

      no_of_pallets: Yup.number()
        .typeError("No of Pallets must be a number")
        .positive("Must be greater than zero")
        .required("No of Pallets is required"),

      total_pallet_weight: Yup.number()
        .typeError("Total Pallets Weight must be a number")
        .positive("Must be greater than zero")
        .required("Total Pallets Weight is required"),

      remarks: Yup.string(),
      // .required('Remarks are required'),
    }),
    onSubmit: async (values) => {
      if (items?.length === 0) {
        Swal.fire({
          icon: "warning",
          // title: "Validation Error",
          text: "At least one item is required for packing",
          confirmButtonColor: Colors?.bg_blue,
        });
        return;
      }

      const hasInvalidPackedQuantity = items?.some(
        (item: any) =>
          item?.packed_quantity === "" ||
          item?.packed_quantity === 0 ||
          item?.packed_quantity === null ||
          item?.packed_quantity === undefined
      );

      if (hasInvalidPackedQuantity) {
        Swal.fire({
          icon: "warning",
          text: "Packed Quantity is required for each item and cannot be 0 or empty",
          confirmButtonColor: Colors?.bg_blue,
        });
        return;
      }

      for (const [index, item] of items.entries()) {
        if (item?.packed_quantity > item?.request_quantity) {
          Swal.fire({
            icon: "warning",
            text: `Item ${
              index + 1
            }: Packed Quantity must be less than Transfer Quantity`,
            confirmButtonColor: Colors?.bg_blue,
          });
          return;
        }
      }

      const formData = new FormData();
      formData.append("packing_date", values?.packing_date ?? "");
      formData.append("packing_status", "packing_done");
      formData.append("no_of_box", values?.no_of_box ?? "");
      formData.append("total_box_weight", values?.total_box_weight ?? "");
      formData.append("no_of_pallets", values?.no_of_pallets ?? "");
      formData.append(
        "total_pallets_weight",
        values?.total_pallet_weight ?? ""
      );
      // new
      formData.append(
        "transfered_from_warehouse_id",
        data?.transfer_from_id ?? ""
      );
      formData.append("storage_location_id", data?.transfer_to_id ?? "");
      formData.append("allocation_packing_type", "Item Request");
      //
      formData.append("item_request_id", id ?? "");
      formData.append("item_request_code", values?.item_request_id ?? "");
      formData.append("remarks", values?.remarks ?? "");
      formData.append("items", JSON.stringify(items));
      formData.append("packing_document", values?.documents ?? "");

      setPageLoading(true);
      WarehouseAxios.post("create/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
        .then((success) => {
          // WarehouseAxios.post('/packing/AllocationPackingDone_packing/create/', formData).then((success) => {
          toast.success("Item Request Packing created successfully");
          navigate("/warehouse-packing-done");
        })
        .catch((error) => {
          const msg = error?.response?.data?.message;
          const errType = error?.response?.data;
          if (msg) {
            toast.error(msg);
          } else if (errType && Object.keys(errType).length) {
            Object.keys(errType).forEach((key) => {
              toast.error(`${errType[key]}`);
            });
          } else {
            toast.error("Something went wrong");
          }
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  useEffect(() => {
    WarehouseAxios.get(`${id}/`)
      .then((success) => {
        type RawItem = {
          item: string;
          created_by?: any;
          updated_by?: any;
          [key: string]: any;
        };

        type CleanItem = Omit<RawItem, "created_by" | "updated_by"> & {
          packed_quantity: number;
          item_id: string;
          remarks: string;
        };

        const updatedArray: CleanItem[] =
          success?.data?.data?.item_data?.map(
            ({ created_by, updated_by, ...item }: RawItem): CleanItem => ({
              ...item,
              packed_quantity: 0,
              item_id: item.item,
              request_quantity: item?.transfer_quantity,
              // subcategory: item?.sub_category_name,
              // category: item?.category_name,
              // make:'test1',
              // model:'test2',
              // barcode_type:'Item',
              remarks: "",
            })
          ) || [];

        formik.setFieldValue(
          "item_request_id",
          success?.data?.data?.item_request_id || ""
        );
        if (success?.data?.data?.item_request_id) setTimeout(() => formik.setFieldError("item_request_id", undefined), 0);
        setData(success?.data?.data || {});
        setItems(updatedArray);
      })
      .catch((error) => {
        toast.error("Something went wrong");
      });
  }, []);

  const handleChange = (idx: number, field: string, value: string) => {
    const updatedItems = [...items];
    let val = field === "remarks" ? value : value === "" ? "" : Number(value);
    updatedItems[idx][field] = val as never;
    setItems(updatedItems);
  };

  return (
    <>
      <div
        className="page-content px-4"
        style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
      >
        <Container
          fluid
          style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 0 15px rgba(0,0,0,0.1)",
          }}
        >
          {pageLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "60vh" }}
            >
              <Spinner animation="border" role="status" variant="primary" />
            </div>
          ) : (
            <>
              <FormBreadcrumb
                formName="Packing"
                isEdit={false}
                navigatePath="/warehouse-packing"
              />
              <Form autoComplete="off" onSubmit={formik.handleSubmit}>
                <Row>
                  {/* <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Packing ID. <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="packing_id"
                    placeholder="Enter Packing ID"
                    value={formik.values.packing_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.packing_id && formik.touched.packing_id}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.packing_id}
                  </Form.Control.Feedback>
                </div>
              </Col> */}

                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Packing Date <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        name="packing_date"
                        value={formik.values.packing_date}
                        onChange={(e) => {
                          formik.handleChange(e);
                          if ((e.target.value || "").trim()) setTimeout(() => formik.setFieldError("packing_date", undefined), 0);
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.packing_date &&
                          formik.touched.packing_date
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.packing_date}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Item Request ID <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        disabled
                        name="item_request_id"
                        placeholder="Enter Item Request ID"
                        value={formik.values.item_request_id}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.item_request_id &&
                          formik.touched.item_request_id
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.item_request_id}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={3}>
                    <FileUploadInput
                      label="Documents"
                      name="documents"
                      formik={formik}
                    />
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Prepared By <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="prepared_by"
                        placeholder="Enter Prepared By"
                        value={formik.values.prepared_by}
                        onChange={(e) => {
                          formik.handleChange(e);
                          if ((e.target.value || "").trim()) setTimeout(() => formik.setFieldError("prepared_by", undefined), 0);
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.prepared_by &&
                          formik.touched.prepared_by
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.prepared_by}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                </Row>
                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(0, 0, 0, 0.8)",
                    margin: "1rem 0",
                  }}
                />
                <Row>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        No of Box <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="no_of_box"
                        placeholder="Enter No of Box"
                        value={formik.values.no_of_box}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            formik.setFieldValue("no_of_box", value);
                            if (value && Number(value) > 0) setTimeout(() => formik.setFieldError("no_of_box", undefined), 0);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.no_of_box && formik.touched.no_of_box
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.no_of_box}
                      </Form.Control.Feedback>
                    </div>
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Total Box Weight <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="total_box_weight"
                        placeholder="Enter Total Box Weight"
                        value={formik.values.total_box_weight}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            formik.setFieldValue("total_box_weight", value);
                            if (value && Number(value) > 0) setTimeout(() => formik.setFieldError("total_box_weight", undefined), 0);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.total_box_weight &&
                          formik.touched.total_box_weight
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.total_box_weight}
                      </Form.Control.Feedback>
                    </div>
                  </Col>

                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        No Of Pallets <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="no_of_pallets"
                        placeholder="Enter No Of Pallets"
                        value={formik.values.no_of_pallets}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            formik.setFieldValue("no_of_pallets", value);
                            if (value && Number(value) > 0) setTimeout(() => formik.setFieldError("no_of_pallets", undefined), 0);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.no_of_pallets &&
                          formik.touched.no_of_pallets
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.no_of_pallets}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Total Pallets Weight
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="total_pallet_weight"
                        placeholder="Enter Total Pallets Weight"
                        value={formik.values.total_pallet_weight}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            formik.setFieldValue("total_pallet_weight", value);
                            if (value && Number(value) > 0) setTimeout(() => formik.setFieldError("total_pallet_weight", undefined), 0);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.total_pallet_weight &&
                          formik.touched.total_pallet_weight
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.total_pallet_weight}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Remarks
                        {/* <span className="text-danger">*</span> */}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="remarks"
                        placeholder="Enter Remarks"
                        value={formik.values.remarks}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.remarks && formik.touched.remarks
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.remarks}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                </Row>
                <div className="mb-4 mt-3" style={{ overflowX: "auto" }}>
                  <table className="table">
                    <thead style={{ backgroundColor: "#E4E4E4" }}>
                      <tr>
                        <th style={{ minWidth: "70px" }}>Sr.No</th>
                        <th style={{ minWidth: "150px" }}>Item Name</th>
                        <th style={{ minWidth: "100px" }}>Unit</th>
                        <th style={{ minWidth: "150px" }}>Category</th>
                        <th style={{ minWidth: "150px" }}>Sub Category</th>
                        <th style={{ minWidth: "150px" }}>Make</th>
                        <th style={{ minWidth: "150px" }}>Model</th>
                        {/* <th style={{ minWidth: "150px" }}>In Stock </th> */}
                        <th style={{ minWidth: "150px" }}>
                          Transfer Quantity{" "}
                        </th>
                        <th style={{ minWidth: "200px" }}>Packed Quantity </th>
                        <th style={{ minWidth: "150px" }}>Remark</th>
                        <th style={{ minWidth: "130px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="align-middle">
                      {items?.map((item: any, idx: number) => (
                        <tr key={item.id}>
                          <td>{idx + 1}</td>
                          <td>{item?.item_name || "-"}</td>
                          <td>{item?.unit_name || item?.unit || "-"}</td>
                          <td>
                            {item?.category_name || item?.category || "-"}
                          </td>
                          <td>
                            {item?.sub_category_name ||
                              item?.subcategory ||
                              "-"}
                          </td>
                          <td>{item?.make || "-"}</td>
                          <td>{item?.model || "-"}</td>
                          <td>{item?.request_quantity || "-"}</td>
                          {/* <td>{item?.packed_quantity || "-"}</td> */}
                          <td>
                            <Form.Control
                              type="text"
                              placeholder="Enter Packed Quantity"
                              value={item.packed_quantity}
                              onChange={(e) => {
                                if (!/^\d*$/.test(e.target.value)) {
                                  return;
                                }
                                handleChange(
                                  idx,
                                  "packed_quantity",
                                  e.target.value
                                );
                              }}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              placeholder="Enter Remark"
                              value={item.remarks}
                              onChange={(e) =>
                                handleChange(idx, "remarks", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            {/* <Button
                        className="btn btn-sm"
                        style={{
                          backgroundColor: "#3F7DDA",
                          color: "#fff",
                          padding: "10px",
                          margin: "0 5px",
                        }}
                      // onClick={() => handleAddStock(item, idx)}
                      >
                        Scan
                      </Button> */}
                            <Button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: Colors?.bg_blue,
                                color: "#fff",
                                padding: "10px",
                                margin: "0 5px",
                              }}
                              onClick={() => handleOpenModal(item)}
                            >
                              Scan
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-center gap-3 py-3">
                  <Button
                    variant="light"
                    onClick={() => navigate(-1)}
                    style={{ border: "1px solid #ced4da" }}
                  >
                    <span
                      // to="/warehosue-packing"
                      className="text-decoration-none"
                      style={{ color: "#495057" }}
                    >
                      Cancel
                    </span>
                  </Button>

                  <Button
                    type="submit"
                    style={{ background: "#3F7DDA", color: "#FFF" }}
                  >
                    Submit
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Container>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>FIFO Enabled</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Serial No.</th>
                <th>Item Code</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{selectedItem?.item_name || "-"}</td>
                <td>{selectedItem?.serial_no || "-"}</td>
                <td>{selectedItem?.item_code || "-"}</td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-center gap-3">
            <Button variant="light" onClick={() => setShowModal(false)}>
              <span
                className="text-decoration-none"
                style={{ color: "#495057" }}
              >
                Close
              </span>
            </Button>
            {/* <Button variant="primary" onClick={() => setShowModal(false)}>
              Submit
            </Button> */}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateWatehouseItemRequestPacking;
